import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Overview({ admin, onNavigate }: { admin: any; onNavigate?: (section: string) => void }) {
  const [stats, setStats] = useState({
    totalProviders: 0,
    activeProviders: 0,
    pendingProviders: 0,
    totalJobs: 0,
    activeJobs: 0,
    completedJobs: 0,
    totalRevenue: 0,
    pendingPayments: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentProviders, setRecentProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    try {
      // Load stats
      const [providersData, jobsData, invoicesData] = await Promise.all([
        supabase.from('provider_profiles').select('status'),
        supabase.from('jobs').select('status'),
        supabase.from('invoices').select('total_amount, status')
      ]);

      if (providersData.data) {
        setStats(prev => ({
          ...prev,
          totalProviders: providersData.data.length,
          activeProviders: providersData.data.filter(p => p.status === 'approved').length,
          pendingProviders: providersData.data.filter(p => p.status === 'pending').length
        }));
      }

      if (jobsData.data) {
        setStats(prev => ({
          ...prev,
          totalJobs: jobsData.data.length,
          activeJobs: jobsData.data.filter(j => ['accepted', 'in_progress'].includes(j.status)).length,
          completedJobs: jobsData.data.filter(j => j.status === 'completed').length
        }));
      }

      if (invoicesData.data) {
        const totalRevenue = invoicesData.data
          .filter(i => i.status === 'paid')
          .reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0);
        const pendingPayments = invoicesData.data
          .filter(i => i.status === 'pending')
          .reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0);
        
        setStats(prev => ({
          ...prev,
          totalRevenue,
          pendingPayments
        }));
      }

      // Load recent jobs
      const { data: jobs } = await supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (jobs) setRecentJobs(jobs);

      // Load recent providers
      const { data: providers } = await supabase
        .from('provider_profiles')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (providers) setRecentProviders(providers);

      setLoading(false);
    } catch (error) {
      console.error('Error loading overview data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line text-4xl text-purple-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
        <p className="text-gray-600 mt-1">Welcome back, {admin?.full_name || admin?.email || 'Admin'}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-100">Total Providers</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalProviders}</p>
              <p className="text-sm text-blue-100 mt-2">
                {stats.activeProviders} active
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-team-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-100">Total Jobs</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalJobs}</p>
              <p className="text-sm text-orange-100 mt-2">
                {stats.activeJobs} active
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-briefcase-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-100">Total Revenue</p>
              <p className="text-3xl font-bold text-white mt-2">£{stats.totalRevenue.toFixed(2)}</p>
              <p className="text-sm text-green-100 mt-2">
                Completed jobs
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-money-pound-circle-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-100">Pending Payments</p>
              <p className="text-3xl font-bold text-white mt-2">£{stats.pendingPayments.toFixed(2)}</p>
              <p className="text-sm text-amber-100 mt-2">
                Awaiting payment
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-2xl text-white"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => onNavigate?.('providers')}
            className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg cursor-pointer font-medium"
          >
            <i className="ri-file-list-3-line text-xl w-5 h-5 flex items-center justify-center"></i>
            <span>New Applications</span>
            {stats.pendingProviders > 0 && (
              <span className="px-2 py-1 bg-white text-orange-600 rounded-full text-xs font-bold animate-pulse">
                {stats.pendingProviders}
              </span>
            )}
          </button>
          <button
            onClick={() => onNavigate?.('clients')}
            className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all shadow-md hover:shadow-lg cursor-pointer font-medium"
          >
            <i className="ri-user-line text-xl w-5 h-5 flex items-center justify-center"></i>
            <span>Manage Clients</span>
          </button>
          <button
            onClick={() => onNavigate?.('services')}
            className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg cursor-pointer font-medium"
          >
            <i className="ri-settings-3-line text-xl w-5 h-5 flex items-center justify-center"></i>
            <span>Manage Services</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Jobs */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-briefcase-line text-xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-white">Recent Jobs</h3>
            </div>
            <button 
              onClick={() => onNavigate?.('jobs')}
              className="text-sm text-white/90 hover:text-white flex items-center gap-1 cursor-pointer transition-colors"
            >
              View All <i className="ri-arrow-right-line"></i>
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {recentJobs.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-briefcase-line text-3xl text-orange-300"></i>
                  </div>
                  <p className="text-gray-500 font-medium">No jobs yet</p>
                  <p className="text-gray-400 text-sm mt-1">Jobs will appear here once created</p>
                </div>
              ) : (
                recentJobs.map((job, index) => (
                  <div 
                    key={job.id} 
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-orange-200 hover:shadow-md transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 font-bold text-sm group-hover:bg-orange-500 group-hover:text-white transition-colors">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{job.service_type}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <i className="ri-user-line text-xs"></i>
                          {job.client_name}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap shadow-sm ${
                      job.status === 'completed' ? 'bg-green-100 text-green-700 border border-green-200' :
                      job.status === 'in_progress' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                      job.status === 'accepted' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                      'bg-gray-100 text-gray-700 border border-gray-200'
                    }`}>
                      <i className={`mr-1 ${
                        job.status === 'completed' ? 'ri-check-line' :
                        job.status === 'in_progress' ? 'ri-loader-4-line' :
                        job.status === 'accepted' ? 'ri-time-line' :
                        'ri-hourglass-line'
                      }`}></i>
                      {job.status?.replace('_', ' ')}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Recent Providers */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-team-line text-xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-white">Recent Applications</h3>
            </div>
            <button 
              onClick={() => onNavigate?.('providers')}
              className="text-sm text-white/90 hover:text-white flex items-center gap-1 cursor-pointer transition-colors"
            >
              View All <i className="ri-arrow-right-line"></i>
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {recentProviders.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-team-line text-3xl text-teal-300"></i>
                  </div>
                  <p className="text-gray-500 font-medium">No applications yet</p>
                  <p className="text-gray-400 text-sm mt-1">Provider applications will appear here</p>
                </div>
              ) : (
                recentProviders.map((provider, index) => (
                  <div 
                    key={provider.id} 
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold text-sm group-hover:bg-teal-500 group-hover:text-white transition-colors uppercase">
                        {provider.full_name?.charAt(0) || 'P'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{provider.full_name}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <i className="ri-mail-line text-xs"></i>
                          {provider.email}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap shadow-sm ${
                      provider.status === 'approved' ? 'bg-green-100 text-green-700 border border-green-200' :
                      provider.status === 'pending' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
                      'bg-red-100 text-red-700 border border-red-200'
                    }`}>
                      <i className={`mr-1 ${
                        provider.status === 'approved' ? 'ri-check-double-line' :
                        provider.status === 'pending' ? 'ri-time-line' :
                        'ri-close-line'
                      }`}></i>
                      {provider.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
